
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppDataContext } from '@/contexts/AppDataContext';

const Company = () => {
  const { data } = useAppDataContext();
  
  // Extract council name from the first payslip
  const councilName = data.payslips.length > 0 ? data.payslips[0].council : 'Mwansabombwe Town Council';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Company Information</h1>
          <p className="text-gray-500">Manage company details and configurations</p>
        </div>
        
        <Tabs defaultValue="general">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="structure">Organization Structure</TabsTrigger>
            <TabsTrigger value="settings">Company Settings</TabsTrigger>
          </TabsList>
          
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>Company Profile</CardTitle>
                <CardDescription>View and manage basic company details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Basic Information</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Company Name</p>
                        <p className="font-medium">{councilName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Registration Number</p>
                        <p className="font-medium">REG-12345-ZM</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Tax Identification Number</p>
                        <p className="font-medium">1000123456</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Industry</p>
                        <p className="font-medium">Local Government</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500">Address</p>
                        <p className="font-medium">Main Street, Mwansabombwe, Luapula Province</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">info@mwansabombwe.gov.zm</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="font-medium">+260 211 123456</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Website</p>
                        <p className="font-medium">www.mwansabombwe.gov.zm</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="structure">
            <Card>
              <CardHeader>
                <CardTitle>Organization Structure</CardTitle>
                <CardDescription>Company hierarchy and organization chart</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Leadership</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="pt-6">
                          <h4 className="font-semibold">Council Chairperson</h4>
                          <p className="text-sm text-gray-500 mt-2">
                            {data.employees.find(emp => emp.jobTitle?.includes('CHAIRPERSON'))?.firstName || 'Terrioh'} {data.employees.find(emp => emp.jobTitle?.includes('CHAIRPERSON'))?.lastName || 'Chungu'}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <h4 className="font-semibold">Council Secretary</h4>
                          <p className="text-sm text-gray-500 mt-2">
                            {data.employees.find(emp => emp.jobTitle?.includes('SECRETARY'))?.firstName || 'Edgar'} {data.employees.find(emp => emp.jobTitle?.includes('SECRETARY'))?.lastName || 'Mulwanda'}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-6">
                          <h4 className="font-semibold">Council Treasurer</h4>
                          <p className="text-sm text-gray-500 mt-2">
                            {data.employees.find(emp => emp.jobTitle?.includes('TREASURER'))?.firstName || 'Mathews'} {data.employees.find(emp => emp.jobTitle?.includes('TREASURER'))?.lastName || 'Mulenga'}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-4">Departments</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Array.from(new Set(data.employees.map(emp => emp.department))).map(dept => (
                        <Card key={dept}>
                          <CardContent className="pt-6">
                            <h4 className="font-semibold">{dept}</h4>
                            <p className="text-sm text-gray-500 mt-2">
                              Staff Count: {data.employees.filter(emp => emp.department === dept).length}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Company Settings</CardTitle>
                <CardDescription>System-wide configuration for your organization</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium mb-4">Financial Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Financial Year</p>
                        <p className="font-medium">January - December</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Currency</p>
                        <p className="font-medium">Zambian Kwacha (ZMW)</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Tax Year</p>
                        <p className="font-medium">January - December</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Payroll Processing Date</p>
                        <p className="font-medium">25th of each month</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-4">HR Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Working Hours</p>
                        <p className="font-medium">8:00 AM - 5:00 PM</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Work Week</p>
                        <p className="font-medium">Monday - Friday</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Leave Year</p>
                        <p className="font-medium">January - December</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Default Leave Days</p>
                        <p className="font-medium">24 days per year</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Company;
